import type { CanvasData, Mask } from '../data'
import { ALL_BORDER_RADIUS_CORNERS, BASE_FLOAT_DEFAULTS, NO_TEXT_HIGHLIGHT } from '../data'

const f = BASE_FLOAT_DEFAULTS
const autoFontFamily = {
	fontFamilyMode: 'auto' as const,
	fontFamilyProvider: 'system' as const,
	fontFamilyGoogleFamily: '',
	fontFamilyCustomUrl: '',
	fontFamilyCustomName: '',
}

const defaultMask: Mask = {
	maskMode: 'none',
	blendMode: 'normal',
	solidColor: { mode: 'foregroundColor', color: '#000000' },
	solidOpacity: 50,
	gradientColorStart: { mode: 'foregroundColor', color: '#000000' },
	gradientColorEnd: { mode: 'foregroundColor', color: '#000000' },
	gradientOpacityStart: 100,
	gradientOpacityEnd: 0,
	gradientAngle: 0,
}

export const customDemo1Template: CanvasData = {
	version: 1,
	layers: {
		'content-main': {
			type: 'content',
			id: 'content-main',
			hidden: false,
			locked: false,
			xPin: 'center',
			yPin: 'bottom',
			textAlignMode: 'value',
			textAlignValue: 'center',
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
				marginLeft: 43,
				marginRight: 43,
				marginTop: 0,
				marginBottom: 0,
			},
			gap: 11,
			textStyle: {
				textColorMode: 'value',
				textColor: { mode: 'foregroundColor', color: '#ffffff' },
				...autoFontFamily,
				fontSizeMode: 'auto',
				fontSizeUnit: 'px',
				fontSizePxValue: 16,
				fontWeightMode: 'auto',
				fontWeightValue: 400,
				fontStyleMode: 'auto',
				fontStyleValue: 'normal',
				lineHeightMode: 'auto',
				lineHeightValue: 1.4,
				textShadowMode: 'auto',
				textShadowValue: 'simple',
				textShadowSimple: { opacity: 20, spread: 5 },
			},
		},
		'text-title': {
			type: 'text',
			id: 'text-title',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'title',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'transparent',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			textStyle: {
				textColorMode: 'value',
				textColor: { mode: 'accentBackgroundColor', color: '#ffffff' },
				...autoFontFamily,
				fontSizeMode: 'value',
				fontSizeUnit: 'px',
				fontSizePxValue: 36,
				fontWeightMode: 'value',
				fontWeightValue: 700,
				fontStyleMode: 'auto',
				fontStyleValue: 'normal',
				lineHeightMode: 'value',
				lineHeightValue: 1.1,
				textShadowMode: 'auto',
				textShadowValue: 'simple',
				textShadowSimple: { opacity: 20, spread: 5 },
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'text-subtitle': {
			type: 'text',
			id: 'text-subtitle',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'subtitle',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'transparent',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			textStyle: {
				textColorMode: 'auto',
				textColor: { mode: 'foregroundColor', color: '#ffffff' },
				...autoFontFamily,
				fontSizeMode: 'value',
				fontSizeUnit: 'px',
				fontSizePxValue: 20,
				fontWeightMode: 'value',
				fontWeightValue: 100,
				fontStyleMode: 'auto',
				fontStyleValue: 'normal',
				lineHeightMode: 'auto',
				lineHeightValue: 1.4,
				textShadowMode: 'auto',
				textShadowValue: 'simple',
				textShadowSimple: { opacity: 20, spread: 5 },
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'text-location': {
			type: 'text',
			id: 'text-location',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'location',
			customValue: '',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 13,
				paddingBottom: 1,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'transparent',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			textStyle: {
				textColorMode: 'value',
				textColor: { mode: 'color', color: '#737a8f' },
				...autoFontFamily,
				fontSizeMode: 'value',
				fontSizeUnit: 'px',
				fontSizePxValue: 15,
				fontWeightMode: 'value',
				fontWeightValue: 400,
				fontStyleMode: 'auto',
				fontStyleValue: 'normal',
				lineHeightMode: 'auto',
				lineHeightValue: 1.4,
				textShadowMode: 'auto',
				textShadowValue: 'simple',
				textShadowSimple: { opacity: 20, spread: 5 },
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'image-company-logo': {
			type: 'image',
			id: 'image-company-logo',
			hidden: false,
			locked: false,
			...f,
			content: 'ref',
			refTo: 'logo',
			customValue: '',
			width: { mode: 'px', pxValue: 64, percentValue: 100 },
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'transparent',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			borderRadius: 10,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
			opacity: 100,
		},
		'layer-1772441408970-1': {
			type: 'substack',
			id: 'layer-1772441408970-1',
			hidden: false,
			locked: false,
			...f,
			direction: 'row',
			alignItems: 'end',
			justifyContent: 'start',
			childSizing: 'auto',
			widthSizing: 'auto',
			gap: 17,
			...autoFontFamily,
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'transparent',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'layer-1772441412723-2': {
			type: 'text',
			id: 'layer-1772441412723-2',
			hidden: false,
			locked: false,
			...f,
			content: 'custom',
			refTo: 'title',
			customValue: '6 weeks paid holiday',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: {
				paddingLeft: 10,
				paddingRight: 10,
				paddingTop: 6,
				paddingBottom: 6,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'color',
				color: '#efe6f8',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			textStyle: {
				textColorMode: 'value',
				textColor: { mode: 'accentBackgroundColor', color: '#000000' },
				...autoFontFamily,
				fontSizeMode: 'value',
				fontSizeUnit: 'px',
				fontSizePxValue: 12,
				fontWeightMode: 'auto',
				fontWeightValue: 400,
				fontStyleMode: 'auto',
				fontStyleValue: 'normal',
				lineHeightMode: 'auto',
				lineHeightValue: 1.4,
				textShadowMode: 'auto',
				textShadowValue: 'simple',
				textShadowSimple: { opacity: 20, spread: 5 },
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'layer-1772441416452-3': {
			type: 'text',
			id: 'layer-1772441416452-3',
			hidden: false,
			locked: false,
			...f,
			content: 'custom',
			refTo: 'title',
			customValue: 'Stress-free rent',
			textAlignMode: 'auto',
			textAlignValue: 'left',
			spacing: {
				paddingLeft: 10,
				paddingRight: 10,
				paddingTop: 6,
				paddingBottom: 6,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 0,
				marginBottom: 0,
			},
			background: {
				mode: 'color',
				color: '#efe6f8',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			textStyle: {
				textColorMode: 'value',
				textColor: { mode: 'accentBackgroundColor', color: '#efe6f8' },
				...autoFontFamily,
				fontSizeMode: 'value',
				fontSizeUnit: 'px',
				fontSizePxValue: 12,
				fontWeightMode: 'auto',
				fontWeightValue: 400,
				fontStyleMode: 'auto',
				fontStyleValue: 'normal',
				lineHeightMode: 'auto',
				lineHeightValue: 1.4,
				textShadowMode: 'auto',
				textShadowValue: 'simple',
				textShadowSimple: { opacity: 20, spread: 5 },
			},
			textHighlight: NO_TEXT_HIGHLIGHT,
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
		},
		'layer-1772455447046-1': {
			type: 'shape',
			id: 'layer-1772455447046-1',
			hidden: false,
			locked: false,
			...f,
			shapeKind: 'rectangle',
			preset: 'locationPin',
			// no fill set; we want the background instead
			fillMode: 'original',
			fill: { mode: 'foregroundColor', color: '#000000' },
			shapeFit: 'contain',
			width: { mode: '%', pxValue: 80, percentValue: 80 },
			height: { mode: 'px', pxValue: 233, percentValue: 100 },
			background: {
				// points to the backdrop image
				mode: 'externalImage',
				color: '#000000',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			borderRadius: 0,
			borderRadiusCorners: ALL_BORDER_RADIUS_CORNERS,
			spacing: {
				paddingLeft: 0,
				paddingRight: 0,
				paddingTop: 0,
				paddingBottom: 0,
				marginLeft: 0,
				marginRight: 0,
				marginTop: 10,
				marginBottom: 0,
			},
		},
		'layer-1772455492391-2': {
			type: 'backdrop',
			id: 'layer-1772455492391-2',
			hidden: false,
			locked: false,
			background: {
				mode: 'color',
				color: '#f8feff',
				imageUrl: '',
				imageScale: 100,
				imageOffsetX: 50,
				imageOffsetY: 50,
			},
			mask: defaultMask,
		},
	},
	layerTree: [
		{
			id: 'content-main',
			children: [
				{ id: 'image-company-logo', children: [] },
				{ id: 'text-location', children: [] },
				{ id: 'text-title', children: [] },
				{ id: 'text-subtitle', children: [] },
				{
					id: 'layer-1772441408970-1',
					children: [
						{ id: 'layer-1772441412723-2', children: [] },
						{ id: 'layer-1772441416452-3', children: [] },
					],
				},
				{ id: 'layer-1772455447046-1', children: [] },
			],
		},
		{ id: 'layer-1772455492391-2', children: [] },
	],
}
